import { useState } from 'react';
import { Check, Trash2, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { HoldToRecordButton } from '../contexts/HoldToRecordContext';
import {
  MEDICATION_DEVIATION_REASONS,
  getDeviationReasonLabel,
} from '../utils/medicationDeviation';

export default function MedicationTaskActionBar({
  onConfirmGiven,
  onSubmitDeviation,
  onDiscard,
  confirmDisabled = false,
  confirmDisabledLabel = null,
  patientId = null,
}) {
  const { t } = useLanguage();
  const [showReasons, setShowReasons] = useState(false);
  const [reasonId, setReasonId] = useState(null);
  const [otherText, setOtherText] = useState('');

  const reasonReady =
    reasonId
    && (reasonId !== 'other' || String(otherText).trim().length > 0);

  const handleNotGiven = () => {
    setShowReasons(true);
  };

  const handleSubmitDeviation = () => {
    if (!reasonReady) return;
    onSubmitDeviation?.({
      reasonId,
      reasonText: getDeviationReasonLabel(t, reasonId, otherText),
      otherText,
    });
  };

  return (
    <div className="shrink-0 rounded-b-[24px] border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      {confirmDisabled && confirmDisabledLabel ? (
        <p className="mb-2 text-center text-xs font-medium text-amber-700">
          {confirmDisabledLabel}
        </p>
      ) : null}

      <div className="flex w-full items-start gap-3">
        <button
          type="button"
          onClick={onDiscard}
          aria-label={t('discard')}
          className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-gray-200"
        >
          <Trash2 className="h-5 w-5" strokeWidth={2} />
        </button>

        <div className="min-w-0 flex-1 space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={confirmDisabled ? undefined : onConfirmGiven}
              disabled={confirmDisabled}
              className={`flex min-h-12 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                confirmDisabled
                  ? 'cursor-not-allowed bg-gray-200 text-gray-500'
                  : 'bg-success text-white hover:bg-success-dark'
              }`}
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
              {t('confirmGiven')}
            </button>

            <button
              type="button"
              onClick={showReasons && reasonReady ? handleSubmitDeviation : handleNotGiven}
              className={`flex min-h-12 flex-1 items-center justify-center gap-1.5 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                showReasons && reasonReady
                  ? 'border-[#DC2626] bg-[#DC2626] text-white hover:bg-[#B91C1C]'
                  : 'border-[#DC2626] bg-white text-[#DC2626] hover:bg-[#FEF2F2]'
              }`}
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
              {showReasons && reasonReady ? t('submitDeviation') : t('notGiven')}
            </button>
          </div>

          {showReasons ? (
            <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {t('selectDeviationReason')}
              </p>
              <div className="flex flex-wrap gap-2">
                {MEDICATION_DEVIATION_REASONS.map((reason) => {
                  const selected = reasonId === reason.id;
                  return (
                    <button
                      key={reason.id}
                      type="button"
                      onClick={() => setReasonId(reason.id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        selected
                          ? 'bg-[#DC2626] text-white'
                          : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {t(reason.labelKey)}
                    </button>
                  );
                })}
              </div>

              {reasonId === 'other' ? (
                <div className="flex items-end gap-2 pt-1">
                  <label className="min-w-0 flex-1 space-y-1">
                    <span className="text-xs font-medium text-gray-600">{t('otherReason')}</span>
                    <textarea
                      value={otherText}
                      onChange={(e) => setOtherText(e.target.value)}
                      rows={2}
                      placeholder={t('speakOrTypeReason')}
                      className="w-full resize-none rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </label>
                  <HoldToRecordButton
                    patientId={patientId}
                    size="compact"
                    anchor="inline"
                    holdOptions={{
                      onTranscript: (transcript) => {
                        const text = String(transcript || '').trim();
                        if (!text) return;
                        setOtherText((prev) => (prev ? `${prev.trim()} ${text}` : text));
                      },
                    }}
                    className="mb-0.5 shrink-0 !h-11 !w-11 !rounded-full"
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
